import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RNButton } from '@/components';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';
import { ConfirmPaymentDetailsModal } from './ConfirmPaymentDetailsModal';
import { PaymentMethodSelector } from '@/components/residents/payments/PaymentMethodSelector';
import { 
  PaymentAmountSection,
  ConvenienceFeesSection,
  PaymentSectionDivider,
} from '@/components/residents/payments/shared';
import { useAppSelector, useAppDispatch } from '@/store';
import { 
  setPayingForLeaseId,
  setAmountToPay,
  setIsCustomAmount,
  setIsPaymentModalOpen,
  updatePaymentForm,
} from '@/store/slices/residents/paymentsSlice';
import {
  selectAmountToPay,
  selectIsCustomAmount,
  selectSelectedPaymentMethod,
} from '@/store/selectors/paymentsSelectors';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { sanitizeCurrencyInput } from '@/utils/common/formatting';
import { useGetLeaseBalanceQuery } from '@/store/api';
import { ChargeBreakdown } from '@/features/payments/components/shared/ChargeBreakdown';

type Params = {
  leaseId: number;
  residentId?: number;
};

export const PaymentSummaryModal: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { leaseId, residentId } = (route.params || {}) as Params;

  // Initialize payment state
  useEffect(() => {
    if (leaseId) {
      dispatch(setPayingForLeaseId(leaseId));
      dispatch(setIsPaymentModalOpen(true));
    }
  }, [leaseId, dispatch]);

  // Fetch balance items for charge breakdown
  const { data: balanceData } = useGetLeaseBalanceQuery(leaseId, { skip: !leaseId });
  const balanceItems = balanceData?.balanceItems;

  // Use centralized payment data
  const paymentData = usePaymentData(residentId);
  const {
    balanceDue,
    selectedPaymentMethod,
    paymentMethods,
    feeStructureDisplay,
    displayName,
    isLoading,
    validation,
    canProceed,
    updateAmountToPay,
    updateIsCustomAmount,
    updateSelectedPaymentMethod,
  } = paymentData;

  // Get state from Redux
  const amountToPay = useAppSelector(selectAmountToPay);
  const isCustomAmount = useAppSelector(selectIsCustomAmount);
  const property = useAppSelector((s) => (s as any).property?.current);

  // Local UI state
  const [otherAmount, setOtherAmount] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState(false);

  // Minimum payment handling now comes from validation.amount.canPayBalanceUnderMin
  const canPayBalanceUnderMin = validation.amount.canPayBalanceUnderMin;

  // Initialize other amount field
  useEffect(() => {
    const fallbackMin = Number(property?.minPaymentAmount ?? 10);
    const defaultCustom = canPayBalanceUnderMin && balanceDue > 0 ? balanceDue : fallbackMin;
    const formattedAmount = String(Number.isFinite(defaultCustom) ? defaultCustom.toFixed(2) : '');
    setOtherAmount(formattedAmount);
    if (!isCustomAmount) {
      updateAmountToPay(balanceDue);
    }
  }, [canPayBalanceUnderMin, balanceDue, property?.minPaymentAmount, isCustomAmount, updateAmountToPay]);

  // Handle payment option changes
  const handlePayBalanceDue = () => {
    updateIsCustomAmount(false);
    updateAmountToPay(balanceDue);
  };

  const handleOtherAmount = () => {
    updateIsCustomAmount(true);
    updateAmountToPay(Number(otherAmount || 0));
  };

  // Handle other amount text changes
  const handleOtherAmountChange = (text: string) => {
    const cleanedText = sanitizeCurrencyInput(text);
    setOtherAmount(cleanedText);
    if (isCustomAmount) {
      updateAmountToPay(Number(cleanedText || 0));
    }
  };

  // Handle payment method management - navigate to "Change your payment method" modal
  const handleManagePaymentMethods = () => {
    // Close current modal first, then navigate to new one
    navigation.replace('AddOrChangePaymentMethod', { 
      leaseId, 
      residentId,
      returnTo: 'PaymentSummaryModal',
      successPage: 'PaymentSummaryModal',
      managePaymentPage: 'ManageMyWallet'
    });
  };

  const onClose = () => {
    dispatch(setIsPaymentModalOpen(false));
    navigation.goBack();
  };
  
  const onContinue = () => setShowConfirm(true);

  return (
    <>
      {!showConfirm && (
        <PaymentModal title="Payment Summary" onClose={onClose} width={420}>
          <PaymentModalContent>
            <ChargeBreakdown
              balanceItems={balanceItems || []}
              balanceDue={balanceDue}
              variant="desktop"
              style={styles.chargesSpacing}
            />

            <PaymentAmountSection
              isCustomAmount={isCustomAmount}
              balanceDue={balanceDue}
              customAmount={otherAmount}
              onSelectBalanceDue={handlePayBalanceDue}
              onSelectCustomAmount={handleOtherAmount}
              onCustomAmountChange={handleOtherAmountChange}
              displayName={displayName}
              canPayBalanceUnderMin={canPayBalanceUnderMin}
              validationError={validation.amount.error}
            />

            <PaymentMethodSelector
              paymentMethods={paymentMethods}
              selectedMethod={selectedPaymentMethod}
              onSelectMethod={updateSelectedPaymentMethod}
              onManagePaymentMethods={handleManagePaymentMethods}
              isLoading={isLoading}
              showChangeButton={true}
              maxHeight={240}
            />

            <PaymentSectionDivider />

            <ConvenienceFeesSection feeStructure={feeStructureDisplay} />

            <View style={styles.footerRow}>
              <RNButton title="Cancel" variant="outlined" onPress={onClose} style={styles.cancelBtn} textStyle={styles.cancelBtnText} />
              <RNButton title="Proceed" variant="primary" onPress={onContinue} disabled={!canProceed} textStyle={styles.proceedBtnText} />
            </View>
          </PaymentModalContent>
        </PaymentModal>
      )}
      {showConfirm && (
        <ConfirmPaymentDetailsModal
          leaseId={leaseId}
          amount={amountToPay}
          payFullAmount={!isCustomAmount}
          paymentMethod={selectedPaymentMethod}
          onBehalfOfName={displayName}
          suppressOverlay={false}
          onRequestClose={onClose}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  cancelBtn: {
    borderColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  cancelBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  proceedBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  chargesSpacing: {
    marginBottom: 16,
  },
});

export default PaymentSummaryModal;


